import { ZIP_TAX_RATES as STATE_TAX_RATES } from './tax-data/state-tax-data';
import { ZIP_TAX_RATES as ZIP_TAX_RATES_5 } from './tax-data/zip-tax-data';

export const estimateTaxRate = (zipcode: string): number => {
	if (!zipcode || zipcode.length < 3) return 0;

	// Try exact 5-digit zip match first (includes local rates)
	if (zipcode.length >= 5) {
		const fiveDigit = zipcode.substring(0, 5);
		const rate = ZIP_TAX_RATES_5[fiveDigit];
		if (rate) return rate;
	}

	// Fallback to 3-digit prefix state-level rates
	const prefix = parseInt(zipcode.substring(0, 3), 10);
	if (isNaN(prefix)) return 0;

	for (const [min, max, rate] of STATE_TAX_RATES) {
		if (prefix >= min && prefix <= max) return rate;
	}
	return 0;
};

export type TaxRateDeal = {
	dealId: string | null;
	zip?: string | null;
	state?: string | null;
};

export type ResolveTaxRateFromDealsParams = {
	deals: Array<TaxRateDeal>;
	/** The design's own deal decides the jurisdiction; falls back to the first available deal. */
	preferredDealId?: string | null;
	fallbackZip?: string | null;
	fallbackState?: string | null;
};

export type ResolvedTaxRate = {
	taxRate: number;
	taxState: string;
	zip: string;
};

/**
 * The ONE jurisdiction-resolution rule shared by the in-tool estimate (getEstimatedTax) and the
 * design-package server (resolveLiveTaxRate): pick the design's deal (else the first), take its zip
 * (else the customer fallback), and derive the rate from the static ZIP table. Never a paid tax API.
 * @param params candidate deals, the preferred deal id, and customer-level zip/state fallbacks
 * @returns the derived rate plus the resolving state and zip
 */
export const resolveTaxRateFromDeals = (params: ResolveTaxRateFromDealsParams): ResolvedTaxRate => {
	const { deals, preferredDealId = null, fallbackZip = '', fallbackState = '' } = params;

	const deal = deals.find((candidate) => candidate.dealId === preferredDealId) ?? deals[0];
	const zip = deal?.zip || fallbackZip || '';
	const state = deal?.state || fallbackState || '';

	return { taxRate: estimateTaxRate(zip), taxState: state, zip };
};
